import { Injectable } from '@angular/core'
import {
  AnnotationsHearingSectionClient,
  AnnotationsHearingSectionsClient,
  HearingSection,
} from '@ftr/annotations-contracts'
import { AnnotationsApiClient, AnnotationsApiClientFactory } from '@ftr/api-annotations'
import { Uuid } from '@ftr/contracts/type/shared'
import { CourtSystemRegionCache } from '@ftr/data-court-system'
import { LocalDate, LocalTime } from '@js-joda/core'
import { firstValueFrom, of } from 'rxjs'

@Injectable({
  providedIn: 'root',
})
export class ManageCourtroomHearingsService {
  constructor(
    private readonly annotationsApiClientFactory: AnnotationsApiClientFactory,
    private readonly courtSystemRegionCache: CourtSystemRegionCache,
  ) {}

  async getSections(
    courtSystemId: Uuid,
    recordingId: Uuid,
  ): Promise<ReturnType<AnnotationsHearingSectionsClient['getHearingSections']>> {
    const client = await this.getApiClient(courtSystemId)

    return client.hearingSections.getHearingSections({
      query: {
        courtSystemId,
        recordingId,
      },
    })
  }

  async getSectionById(
    courtSystemId: Uuid,
    hearingSectionId: Uuid,
  ): Promise<ReturnType<AnnotationsHearingSectionClient['getHearingSection']>> {
    const client = await this.getApiClient(courtSystemId)

    return client.hearingSection.getHearingSection({
      query: {
        hearingSectionId,
      },
    })
  }

  async startBlankHearing(
    courtSystemId: Uuid,
    locationId: Uuid,
    startTime: LocalTime,
    recordingDate: LocalDate,
    courtroomSessionId: Uuid | undefined,
  ): Promise<ReturnType<AnnotationsApiClient['courtroomControls']['triggerHearingSection']>> {
    const client = await this.getApiClient(courtSystemId)

    return client.courtroomControls.triggerHearingSection({
      body: {
        caseReference: null,
        courtSystemId,
        locationId,
        startDateTime: startTime.atDate(recordingDate),
        title: null,
        courtroomSessionId,
      },
    })
  }

  async updateHearing(
    courtSystemId: Uuid,
    locationId: Uuid,
    updatedHearingSection: HearingSection,
    recordingDate: LocalDate,
    courtroomSessionId: Uuid | undefined,
  ): Promise<ReturnType<AnnotationsHearingSectionClient['updateHearingSection']>> {
    const client = await this.getApiClient(courtSystemId)

    return client.hearingSection.updateHearingSection({
      body: {
        id: updatedHearingSection.id,
        startTime: updatedHearingSection.startTime,
        endTime: updatedHearingSection.endTime,
        title: updatedHearingSection.title ?? null,
        caseReference: updatedHearingSection.caseReference ?? null,
        recordingDate,
        locationId,
        isAutoGenerated: false,
        courtroomSessionId,
      },
    })
  }

  private async getApiClient(courtSystemId: Uuid): Promise<AnnotationsApiClient> {
    const { region } = await firstValueFrom(this.courtSystemRegionCache.getOrFetchCourtSystem(of(courtSystemId)))
    return this.annotationsApiClientFactory.build(region)
  }
}
